<template>
  <div class="article-comment-item">
    <div class="logo">
      <BaseIconSvoi icon="users/user" size="s" />
    </div>

    <div class="content">
      <div class="meta">
        <div class="author">{{ comment.user }}</div>
        <div class="date">{{ comment.created_at }}</div>
      </div>
      <div v-if="comment.text" class="text">
        {{ comment.text }}
      </div>

      <div v-if="comment.images && comment.images.length" class="images-gallery">
        <div
          v-for="(imagePath, index) in comment.images"
          :key="index"
          class="image-item"
          @click="openImageViewer(index)"
        >
          <img
            :src="imagePath"
            :alt="`Изображение ${index + 1} к комментарию`"
            loading="lazy"
            class="comment-image"
          />
          <div class="image-overlay">
            <BaseIconSvoi icon="search/search-zoom-in" size="s" />
          </div>
        </div>
      </div>
    </div>

    <ClientOnly>
      <ArticleCommentImageViewer
        v-model="isImageViewerOpen"
        :images="comment.images || []"
        :initial-index="selectedImageIndex"
      />
    </ClientOnly>
  </div>
</template>

<script lang="ts" setup>
  import { BaseIconSvoi } from '~/components/base-ui/icon'
  import type { ArticleComment } from '~/models/articles'
  import ArticleCommentImageViewer from './ArticleCommentImageViewer.vue'

  defineProps<{
    comment: ArticleComment
  }>()

  const isImageViewerOpen = ref(false)
  const selectedImageIndex = ref(0)

  const openImageViewer = (index: number) => {
    selectedImageIndex.value = index
    isImageViewerOpen.value = true
  }
</script>

<style lang="scss" scoped>
  .article-comment-item {
    display: flex;
    gap: 12px;
    padding-bottom: 8px;
    border-bottom: 1px solid $color-potato-20;

    @include vp-767 {
      gap: 8px;
    }

    > .logo {
      overflow: hidden;
      display: flex;
      align-items: center;
      justify-content: center;
      min-width: 56px;
      height: 56px;
      border-radius: 50%;
      background-color: $color-potato-20;

      @include vp-1279 {
        min-width: 48px;
        height: 48px;
      }

      @include vp-767 {
        min-width: 32px;
        height: 32px;
      }
    }

    > .content {
      @extend %flex-column;

      gap: 8px;

      @include vp-767 {
        gap: 6px;
      }
    }

    > .content > .meta {
      display: flex;
      align-items: center;
      gap: 8px;

      > .author {
        font-size: 12px;
        font-weight: 500;
        line-height: 16px;
      }

      > .date {
        font-size: 10px;
        line-height: 14px;
        color: $color-potato-70;
      }
    }

    > .content > .text {
      font-size: 14px;
      line-height: 20px;

      @include vp-767 {
        font-size: 12px;
        line-height: 16px;
      }
    }

    > .content > .images-gallery {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      margin-top: 4px;

      @include vp-767 {
        gap: 4px;
      }

      > .image-item {
        position: relative;
        border-radius: 8px;
        overflow: hidden;
        max-width: 120px;
        cursor: pointer;
        transition: all 0.2s ease;

        @include vp-767 {
          max-width: 100px;
        }

        &:hover {
          transform: scale(1.02);
          box-shadow: 0 4px 12px rgb(0 0 0 / 15%);

          > .image-overlay {
            opacity: 1;
          }
        }

        > .comment-image {
          width: 100%;
          height: 80px;
          object-fit: cover;
          border: 1px solid $color-potato-20;
          border-radius: 8px;
          transition: all 0.2s ease;

          @include vp-767 {
            height: 70px;
          }
        }

        > .image-overlay {
          position: absolute;
          inset: 0;
          background: rgb(0 0 0 / 50%);
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transition: opacity 0.2s ease;
          border-radius: 8px;
          color: $color-default-white;
        }
      }
    }
  }
</style>
